import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { ExpandedTooltip, Icon } from '@livable/saas-react-components';
import { Button } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { formatUtilityLabel, getExtractedTotalAmount } from 'src/pages/NewBillingModule/ApplyBillingFull/BillAutomationExtraction';
import { APPFOLIO_PUSH_STATUS, AppfolioPushMessages } from 'src/pages/NewBillingModule/ApplyBillingFull/AppfolioPush';
import { getAppfolioPushChipProps, getAppfolioPushMenuHint } from '../appfolioPushRow';
import useStyles from './billDetailsStyles';
import { DASH, formatDate, formatMoney, getCommonServiceDates, getServicePeriods, pickValue } from './billDetailsUtils';
import { normalizeAmounts, withSourceIndexes } from './billEditUtils';
import { BillSummaryEditTable } from './BillSummaryEditTable';
import { getServiceIndexes, getServiceTypes, matchBillUtilities, selectServices } from './billUtilityMatching';
import { ApGlAccountValue } from './ApGlAccountValue';
import { ConfirmActionModal } from './ConfirmActionModal';
import { ExtraUtilitiesCard } from './ExtraUtilitiesCard';
import { PropertyLineItems } from './PropertyLineItems';

/** What the action does, in its own terms — not as an exception to allocating. */
const PUSH_HINT = 'Send this bill to AppFolio as an Accounts Payable record. This does not affect resident charges.';

/**
 * One document is one invoice and one payable, whatever it covers — so the confirmation is about the
 * whole bill, and it says the parts that cannot be taken back from here.
 */
const PUSH_CONFIRM_MESSAGE = 'This creates one Accounts Payable bill in AppFolio for the whole invoice, for the amount the ' + 'document states. It cannot be undone from Livable, and the bill cannot be pushed again. Check the ' + 'bill details and utility mappings before continuing.';

/** The Livable property each block of the bill was matched to, for reading its GL account back. */
const getPropertyLivableId = (data, propertyIndex) => {
  var _data$properties;
  return (data === null || data === void 0 || (_data$properties = data.properties) === null || _data$properties === void 0 || (_data$properties = _data$properties[propertyIndex]) === null || _data$properties === void 0 ? void 0 : _data$properties.propertyId) || '';
};

/**
 * What the summary calls the account. Once pushed, it is the one posted to. Before that it is the one
 * each line resolves to, unless a line could not be told its utility — then it is its property's
 * default, and saying so is the only honest name for it.
 */
function getApGlSummaryLabel(apGlAccounts, pairs) {
  var _apGlAccounts$isOnPro;
  if (apGlAccounts !== null && apGlAccounts !== void 0 && apGlAccounts.isRecorded) return 'Accounts Payable GL Account (Expense), as pushed:';
  if (apGlAccounts !== null && apGlAccounts !== void 0 && (_apGlAccounts$isOnPro = apGlAccounts.isOnPropertyDefault) !== null && _apGlAccounts$isOnPro !== void 0 && _apGlAccounts$isOnPro.call(apGlAccounts, pairs)) return 'Default Accounts Payable GL Account (Expense):';
  return 'Accounts Payable GL Account (Expense):';
}

/**
 * Extracted bill summary and per-property line items (Figma: "Bill details" card), covering only the
 * utilities the matched property is set up for. Editing works on a draft that recalculates as it is
 * typed in; saving hands it to `onSave`. Whatever else the bill charges for follows in its own card.
 */
export function BillDetailsCard(_ref) {
  var _view$finalValidation;
  let apGlAccounts = _ref.apGlAccounts,
    billPropertyIds = _ref.billPropertyIds,
    customerName = _ref.customerName,
    data = _ref.data,
    _ref$editBlockedReaso = _ref.editBlockedReason,
    editBlockedReason = _ref$editBlockedReaso === void 0 ? null : _ref$editBlockedReaso,
    _ref$isAppfolio = _ref.isAppfolio,
    isAppfolio = _ref$isAppfolio === void 0 ? false : _ref$isAppfolio,
    _ref$isInvoiceNumberL = _ref.isInvoiceNumberLocked,
    isInvoiceNumberLocked = _ref$isInvoiceNumberL === void 0 ? false : _ref$isInvoiceNumberL,
    _ref$needsProperty = _ref.needsProperty,
    needsProperty = _ref$needsProperty === void 0 ? false : _ref$needsProperty,
    _ref$hideMatch = _ref.hideMatch,
    hideMatch = _ref$hideMatch === void 0 ? false : _ref$hideMatch,
    _ref$startEditing = _ref.startEditing,
    startEditing = _ref$startEditing === void 0 ? false : _ref$startEditing,
    _ref$editTitle = _ref.editTitle,
    editTitle = _ref$editTitle === void 0 ? 'Edit bill details' : _ref$editTitle,
    _ref$saveLabel = _ref.saveLabel,
    saveLabel = _ref$saveLabel === void 0 ? 'Save' : _ref$saveLabel,
    onCancel = _ref.onCancel,
    onPush = _ref.onPush,
    onSave = _ref.onSave,
    properties = _ref.properties,
    _ref$pushRefusalCode = _ref.pushRefusalCode,
    pushRefusalCode = _ref$pushRefusalCode === void 0 ? null : _ref$pushRefusalCode,
    _ref$pushState = _ref.pushState,
    pushState = _ref$pushState === void 0 ? null : _ref$pushState,
    _ref$showPush = _ref.showPush,
    showPush = _ref$showPush === void 0 ? false : _ref$showPush;
  const classes = useStyles();
  // Stamped once, when the form first opens: the indexes describe the payload it opened on for the whole of its life.
  const _useState = useState(() => startEditing && data ? withSourceIndexes(data) : null),
    _useState2 = _slicedToArray(_useState, 2),
    draft = _useState2[0],
    setDraft = _useState2[1];
  /** A second click would write a second trail entry and then be refused as stale. */
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isSaving = _useState4[0],
    setIsSaving = _useState4[1];
  const _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    isPushConfirmOpen = _useState6[0],
    setIsPushConfirmOpen = _useState6[1];
  const isEditing = draft !== null;
  const view = isEditing ? draft : data;
  const _useMemo = useMemo(() => {
      const matched = matchBillUtilities({
        billPropertyIds,
        data: view,
        properties
      });
      return {
        covered: selectServices(matched, true),
        extra: selectServices(matched, false),
        groups: matched
      };
    }, [billPropertyIds, properties, view]),
    covered = _useMemo.covered,
    extra = _useMemo.extra,
    groups = _useMemo.groups;
  const summary = useMemo(() => {
    var _pickValue, _pickValue2;
    const serviceTypes = getServiceTypes(covered);
    const coveredServices = covered.flatMap(group => group.services.map(item => item.service));
    return {
      accountNumber: (_pickValue = pickValue(view, ['accountNumber'])) !== null && _pickValue !== void 0 ? _pickValue : DASH,
      // Charges that belong to the bill rather than to any one utility. A late fee is lifted off
      // the service it was printed beside during extraction (LB-5845) -- it is charged for paying
      // late, not for water, and leaving it there bills a resident for it. But the total still
      // includes it, so without showing it here the bill adds up to more than everything on
      // screen, with nothing saying why.
      //
      // `billCharges`, not `lineItems`: the latter looks like the bill-level list and is not. The
      // service refills it with a flattened copy of every service line on any bill narrowed to the
      // utilities it was uploaded for, so reading it here would print the whole bill a second time.
      billLevelCharges: ((view === null || view === void 0 ? void 0 : view.billCharges) || []).filter(line => line && (line.description || line.amount !== null)).map(line => ({
        label: line.description || 'Other charge',
        value: formatMoney(line.amount, view === null || view === void 0 ? void 0 : view.currency)
      })),
      addresses: ((view === null || view === void 0 ? void 0 : view.properties) || []).map(property => property.address).filter(Boolean),
      billDate: formatDate(pickValue(view, ['billDate', 'postingDate'])),
      customFields: ((view === null || view === void 0 ? void 0 : view.customFields) || []).filter(field => field.label || field.value),
      dueDate: formatDate(pickValue(view, ['dueDate', 'billDueDate'])),
      invoiceNumber: (_pickValue2 = pickValue(view, ['invoiceNumber', 'invoiceNo'])) !== null && _pickValue2 !== void 0 ? _pickValue2 : DASH,
      provider: (view === null || view === void 0 ? void 0 : view.vendorName) || DASH,
      serviceDates: getCommonServiceDates(coveredServices, view),
      servicePeriods: getServicePeriods(coveredServices, view),
      serviceTypes,
      totalDue: formatMoney(getExtractedTotalAmount(view), view === null || view === void 0 ? void 0 : view.currency),
      utilities: serviceTypes.length ? serviceTypes.map(formatUtilityLabel).join(', ') : DASH
    };
  }, [covered, view]);
  const isVerified = (view === null || view === void 0 || (_view$finalValidation = view.finalValidation) === null || _view$finalValidation === void 0 ? void 0 : _view$finalValidation.isValid) === true;
  // Our own sentence, never the reader's `finalValidation.reason`: that names the reader that read the
  // bill and carries its technical detail ("No vision pass: retry started at …"), LB-5918.
  const validationReason = isVerified ? 'Service provider, utilities, and address match with existing property records.' : 'Some validation checks did not pass. Review the extracted values before continuing.';

  /**
   * The draft stays open when the save is refused — the bill was allocated or pushed while the
   * drawer sat open, or somebody else changed it first — because closing it would throw away the
   * very work the refusal is about. `!== false` so a caller that answers nothing still closes.
   */
  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      const outcome = await onSave(normalizeAmounts(draft));
      if (outcome !== false) setDraft(null);
    } finally {
      setIsSaving(false);
    }
  };
  const handleConfirmPush = () => {
    setIsPushConfirmOpen(false);
    onPush();
  };

  /**
   * Before a push: the account a payable for this bill *would* post to, resolved per line the way the
   * push resolves it — a utility configured with its own account posts there, the rest to their
   * property's default (LB-5980). After one: the account it *did* post to, read back from what was sent.
   *
   * Over every line, the other utilities' too: one the property is not set up for has no account, and the
   * push blocks on it, so the bill cannot be said to post to the account the rest share.
   */
  const billPairs = groups.flatMap(group => group.services.map(_ref2 => {
    let service = _ref2.service;
    return {
      propertyId: getPropertyLivableId(view, group.propertyIndex),
      utilityId: service === null || service === void 0 ? void 0 : service.utilityId
    };
  })).filter(pair => pair.propertyId);
  const apGlLabel = apGlAccounts !== null && apGlAccounts !== void 0 && apGlAccounts.isEnabled ? apGlAccounts.getSummaryLabel(billPairs) : null;
  const apGlSummaryLabel = getApGlSummaryLabel(apGlAccounts, billPairs);
  // Where the whole bill has no account, it points at the one property to fix; with several, each line
  // points at its own.
  const billPropertyLivableIds = [...new Set(billPairs.map(pair => pair.propertyId))];
  const apGlSetupPropertyId = billPropertyLivableIds.length === 1 ? billPropertyLivableIds[0] : null;

  /** One property's AP GL column: each line's own account, as the push resolves it. */
  const getGlAccountLabelFor = propertyIndex => apGlAccounts !== null && apGlAccounts !== void 0 && apGlAccounts.isEnabled ? service => apGlAccounts.getLineLabel(getPropertyLivableId(view, propertyIndex), service === null || service === void 0 ? void 0 : service.utilityId) : null;
  const pushChip = pushState ? getAppfolioPushChipProps(pushState) : null;
  const isPushed = (pushState === null || pushState === void 0 ? void 0 : pushState.status) === APPFOLIO_PUSH_STATUS.succeeded;
  const pushDisabledHint = pushState ? getAppfolioPushMenuHint(pushState) : null;
  const emptyStateMessage = ((view === null || view === void 0 ? void 0 : view.properties) || []).length ? 'None of the utilities on this bill are set up on the properties it matched.' : 'No property line items were extracted for this bill.';
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.card, " ").concat(isEditing ? classes.cardOverflowing : '')
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.cardHeader
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.cardHeaderTitles
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.cardTitle
  }, isEditing ? editTitle : 'Bill details'), !isEditing && isAppfolio && pushChip ? /*#__PURE__*/React.createElement("span", {
    className: "".concat(classes.statusChip, " ").concat(isPushed ? classes.statusChipDone : '')
  }, pushChip.label) : null), isEditing ? null :
  /*#__PURE__*/
  // The reason hangs on the wrapper: a disabled button hears no pointer events of its own, so
  // a title on it would never be read out.
  React.createElement(ExpandedTooltip, {
    placement: "top",
    title: editBlockedReason !== null && editBlockedReason !== void 0 ? editBlockedReason : '',
    type: "grey"
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("button", {
    "aria-label": "Edit bill details",
    className: classes.iconButton,
    disabled: !onSave || !!editBlockedReason
    // Stamped here and nowhere else: this is the moment the draft opens, and the
    // indexes have to describe the payload it opened on for the whole of its life.
    ,
    onClick: () => setDraft(withSourceIndexes(data)),
    type: "button"
  }, /*#__PURE__*/React.createElement(Icon, {
    state: "grey",
    type: "edit"
  }))))), isEditing ? /*#__PURE__*/React.createElement(BillSummaryEditTable, {
    customerName: customerName,
    isInvoiceNumberLocked: isInvoiceNumberLocked,
    onChange: setDraft,
    serviceDates: summary.serviceDates,
    serviceTypes: summary.serviceTypes,
    value: draft
  }) : /*#__PURE__*/React.createElement("div", {
    className: classes.summary
  }, needsProperty || hideMatch ? null : /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.matchBox, " ").concat(isVerified ? '' : classes.matchBoxWarning)
  }, isVerified ? /*#__PURE__*/React.createElement(Icon, {
    type: "check-circle"
  }) : /*#__PURE__*/React.createElement(Icon, {
    state: "warning",
    type: "warning"
  }), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.matchTitle, " ").concat(isVerified ? '' : classes.matchTitleWarning)
  }, isVerified ? 'Match: Verified' : 'Match: Review recommended'), /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.matchText, " ").concat(isVerified ? '' : classes.matchTextWarning)
  }, validationReason))), /*#__PURE__*/React.createElement("div", {
    className: classes.sectionLabel
  }, "Summary:"), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryList
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Customer:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, (view === null || view === void 0 ? void 0 : view.customerName) || customerName || DASH)), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Service provider:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, summary.provider)), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Utilities:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, summary.utilities)), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Properties:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, summary.addresses.length ? summary.addresses.map(address => /*#__PURE__*/React.createElement("div", {
    key: address
  }, address)) : DASH))), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryList
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Account #:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, summary.accountNumber)), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Invoice #:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, summary.invoiceNumber)), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Bill date:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, summary.billDate)), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Due date:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, summary.dueDate)), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Service dates:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, summary.servicePeriods.length ? summary.servicePeriods.map(entry => /*#__PURE__*/React.createElement("div", {
    key: entry.period
  }, summary.servicePeriods.length > 1 ? "".concat(entry.utilities, ": ").concat(entry.period) : entry.period)) : DASH))), apGlLabel ? /*#__PURE__*/React.createElement("div", {
    className: classes.summaryList
  }, /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.summaryRow, " ").concat(classes.summaryRowStacked)
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabelStacked
  }, apGlSummaryLabel), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, /*#__PURE__*/React.createElement(ApGlAccountValue, {
    label: apGlLabel,
    propertyLivableId: apGlSetupPropertyId
  })))) : null, summary.customFields.length ? /*#__PURE__*/React.createElement("div", {
    className: classes.summaryList
  }, summary.customFields.map((field, index) => /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow,
    key: "".concat(field.label, "-").concat(index)
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabelWide
  }, field.label || DASH, ":"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, field.value || DASH)))) : null, summary.billLevelCharges.length ? /*#__PURE__*/React.createElement("div", {
    className: classes.summaryList
  }, summary.billLevelCharges.map((charge, index) => /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow,
    key: "".concat(charge.label, "-").concat(index)
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabelWide
  }, charge.label, ":"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, charge.value)))) : null, /*#__PURE__*/React.createElement("div", {
    className: classes.summaryList
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Total due:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, summary.totalDue))), /*#__PURE__*/React.createElement("div", {
    className: classes.sectionLabel
  }, "Line items by property:")), isEditing ? /*#__PURE__*/React.createElement("div", {
    className: classes.editSectionRow
  }, "Line items per property:") : null, covered.length ? covered.map(group => /*#__PURE__*/React.createElement(PropertyLineItems, {
    currency: view === null || view === void 0 ? void 0 : view.currency,
    data: view
    // Its own line's account, not the bill's: two properties on one bill can post to different
    // ones, and so can two utilities of one property configured apart. Once pushed, this is the
    // account that property's lines actually posted to.
    ,
    getGlAccountLabel: getGlAccountLabelFor(group.propertyIndex),
    glAccountPropertyId: getPropertyLivableId(view, group.propertyIndex),
    isEditing: isEditing,
    key: group.propertyIndex,
    onChange: setDraft,
    propertyIndex: group.propertyIndex,
    serviceIndexes: getServiceIndexes(group),
    showGlCode: !!(apGlAccounts !== null && apGlAccounts !== void 0 && apGlAccounts.isEnabled)
  })) : /*#__PURE__*/React.createElement("div", {
    className: classes.emptyState
  }, emptyStateMessage), isEditing ? /*#__PURE__*/React.createElement("div", {
    className: classes.editFooter
  }, /*#__PURE__*/React.createElement(Button, {
    disabled: isSaving,
    onClick: () => {
      setDraft(null);
      onCancel === null || onCancel === void 0 || onCancel();
    },
    size: "small",
    variant: "outlined"
  }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
    disabled: isSaving,
    onClick: handleSave,
    size: "small",
    variant: "contained"
  }, isSaving ? 'Saving…' : saveLabel)) : null, !isEditing && isAppfolio && pushState ? /*#__PURE__*/React.createElement("div", {
    className: classes.cardActions
  }, /*#__PURE__*/React.createElement(AppfolioPushMessages, {
    blockers: pushState.blockers,
    className: classes.cardActionsMessages,
    disabledReason: null,
    liveAttempt: pushState.liveAttempt,
    pushStatus: pushState.pushStatus,
    refusalCode: pushRefusalCode
  }), showPush ?
  /*#__PURE__*/
  // A disabled button hears no hover, so the reason it is disabled hangs on the wrapper.
  React.createElement(ExpandedTooltip, {
    placement: "top",
    title: pushDisabledHint || PUSH_HINT,
    type: "grey"
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Button, {
    disabled: !onPush || !pushState.canPush,
    onClick: () => setIsPushConfirmOpen(true),
    size: "small",
    variant: "contained"
  }, isPushed ? 'Pushed to AppFolio' : 'Push to AppFolio'))) : null, /*#__PURE__*/React.createElement(ConfirmActionModal, {
    confirmLabel: "Push to AppFolio",
    isOpen: isPushConfirmOpen,
    message: PUSH_CONFIRM_MESSAGE,
    onClose: () => setIsPushConfirmOpen(false),
    onConfirm: handleConfirmPush,
    title: "Push bill to AppFolio?"
  })) : null), extra.length ? /*#__PURE__*/React.createElement(ExtraUtilitiesCard, {
    currency: view === null || view === void 0 ? void 0 : view.currency,
    data: view,
    getGlAccountLabelFor: getGlAccountLabelFor,
    groups: extra,
    showGlCode: !!(apGlAccounts !== null && apGlAccounts !== void 0 && apGlAccounts.isEnabled)
  }) : null);
}